/**
 * The app's texts in English: the reference for the other languages, which must have every key
 * (a missing one fails the typecheck). `{name}` marks a value filled in by the app.
 */
export const en = {
  // Language picker
  'language.label': 'Language',

  // Login and sign-up
  'auth.login': 'Log in',
  'auth.signup': 'Create account',
  'auth.signupTitle': 'Create your account',
  'auth.signupLead': 'Create your account, confirm your email, and your free trial can start straight away.',
  'auth.loginLead': 'Log in with your email, or the username we sent you.',
  'auth.passwordChanged': 'Password changed. Log in with your new password.',
  'auth.name': 'Your name',
  'auth.email': 'Email',
  'auth.emailOrUsername': 'Email or username',
  'auth.whatsapp': 'WhatsApp number',
  'auth.whatsappHint': 'So we can reach you on WhatsApp.',
  'auth.password': 'Password',
  'auth.keepLoggedIn': 'Keep me logged in on this device',
  'auth.wait': 'Please wait…',
  'auth.forgot': 'Forgot your password?',
  'auth.enterName': 'Enter your name.',
  'auth.weakPassword': 'Choose a stronger password: it needs every item in the list under the password.',
  'auth.deviceUnsupported': 'This device is not supported.',
  'auth.loginAgain': 'Log in again.',
  'auth.accountDeleted': 'Your account has been deleted.',

  // Password rules
  'password.rules': 'Password requirements',
  'password.length': 'At least 8 characters',
  'password.upper': 'An uppercase letter',
  'password.lower': 'A lowercase letter',
  'password.number': 'A number',
  'password.done': '(done)',
  'password.missing': '(missing)',

  // Phone field
  'phone.country': 'Country: {name}, +{dial}',
  'phone.placeholder': '7700 900123',
  'phone.search': 'Search your country',
  'phone.countries': 'Countries',
  'phone.noCountry': 'No country found',

  // Password reset
  'reset.title': 'Reset your password',
  'reset.sent': 'If {email} has an account, we sent it a 6-digit code. Enter it with your new password.',
  'reset.lead': 'Enter your email and we will send you a code.',
  'reset.code': 'Code',
  'reset.newPassword': 'New password',
  'reset.save': 'Save new password',
  'reset.send': 'Send code',
  'reset.back': 'Back to log in',

  // Channel languages (the list's languages)
  'catalog.title': 'Which languages do you watch?',
  'catalog.lead': 'Your channels, movies and series will be in these languages, so the app loads quickly on your phone and TV.',
  'catalog.continue': 'Continue',
  'catalog.languages': 'Languages',
  'catalog.more': 'More languages…',
  'catalog.international': 'Also international movies and series',
  'catalog.internationalHint': '(no particular language, e.g. "Netflix movies": makes the list bigger)',
  'catalog.note': "Today's matches on Home stay available in every language. You can change this any time in Account.",
  'catalog.saving': 'Saving…',
  'catalog.chooseOne': 'Choose at least one language',

  // Email confirmation
  'verify.title': 'Confirm your email',
  'verify.lead': "We sent a 6-digit code to {email}. Enter it below. Check your spam folder if you don't see it.",
  'verify.code': 'Code',
  'verify.newCodeSent': 'A new code is on its way.',
  'verify.checking': 'Checking…',
  'verify.confirm': 'Confirm',
  'verify.resend': 'Send a new code',

  // Screens without access
  'noAccess.noPlan.title': 'Start your free trial',
  'noAccess.noPlan.body': 'Ask for your free trial and watch everything on this device. Most trials start straight away; your time starts when you first press play.',
  'noAccess.noPlan.contact': 'Hello, I would like to subscribe to IPTViva.',
  'noAccess.pending.title': 'Trial requested',
  'noAccess.pending.body': 'We received your request and will activate your free trial shortly. This screen opens the channels as soon as it is active.',
  'noAccess.pending.contact': 'Hello, I requested a free trial on IPTViva.',
  'noAccess.used.title': 'Free trial already used',
  'noAccess.used.body': 'This device, number or email has already had a free trial. Subscribe to keep watching.',
  'noAccess.used.contact': 'Hello, my free trial is used and I would like to subscribe to IPTViva.',
  'noAccess.trialEnded.title': 'Your free trial has ended',
  'noAccess.trialEnded.body': 'We hope you enjoyed it. Subscribe to keep watching: Standard for one screen, Premium for two at the same time.',
  'noAccess.trialEnded.contact': 'Hello, my free trial has ended and I would like to subscribe to IPTViva.',
  'noAccess.expired.title': 'Your subscription has ended',
  'noAccess.expired.body': 'Renew your subscription to keep watching.',
  'noAccess.expired.contact': 'Hello, I would like to renew my IPTViva subscription.',
  'noAccess.sending': 'Sending…',
  'noAccess.request': 'Request free trial',
  'noAccess.waiting': 'Waiting for activation…',
  'noAccess.contactWhatsapp': 'Contact us on WhatsApp',
  'noAccess.subscribeWhatsapp': 'Subscribe on WhatsApp',
  'noAccess.loggedInAs': 'Logged in as {name}',
  'common.logOut': 'Log out',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.tryAgain': 'Try again',

  // Account deletion
  'delete.link': 'Delete my account',
  'delete.title': 'Delete your account?',
  'delete.body': 'Your account and its details are deleted for good. Enter your password to confirm.',
  'delete.deleting': 'Deleting…',
  'delete.confirm': 'Delete account',

  // Session problems
  'unverified.title': "Can't verify your access",
  'unavailable.title': 'IPTViva is unavailable',
  'unavailable.device': 'This device could not be identified. Restart the app and try again.',
  'unavailable.build': 'This build has no account server configured (VITE_ACCOUNT_API).',

  // Messages from the account server, by error code
  'error.invalid-credentials': 'Wrong email, username or password.',
  'error.account-disabled': 'This account has been disabled. Contact us.',
  'error.trial-used-on-device': 'This device has already had a free trial. Log in with a subscribed account, or contact us to subscribe.',
  'error.trial-bound-to-other-device': 'Your free trial is already in use on your other device. Subscribe to watch on more devices.',
  'error.device-limit': 'This account is already used on all the devices its plan allows. To use it on a new device, contact support.',
  'error.unsupported-device': 'This device is not supported.',
  'error.rate-limited': 'Too many attempts. Wait a while and try again.',
  'error.session-ended': 'You have been logged out. Log in again.',
  'error.invalid-email': 'Enter a valid email address.',
  'error.disposable-email': 'Temporary email addresses are not accepted. Use your usual email.',
  'error.invalid-phone': 'Enter your WhatsApp number with the country code.',
  'error.weak-password': 'Choose a stronger password: at least 8 characters, with an uppercase letter, a lowercase letter and a number.',
  'error.missing-name': 'Enter your name.',
  'error.email-taken': 'An account with this email already exists. Log in instead.',
  'error.wrong-password': 'Wrong password.',
  'error.invalid-code': 'Wrong or expired code. Check the email or ask for a new code.',
  'error.code-too-soon': 'A code was just sent. Wait a minute before asking for another one.',
  'error.not-active': 'Your account has no active plan.',
  'error.email-unavailable': 'Password reset by email is not available yet. Contact us on WhatsApp and we will send you a new password.',
  'error.invalid-input': 'Choose at least one language.',
  'error.server-error': 'Something went wrong. Try again.',
  'error.offline': 'Could not reach the server. Check your internet connection.',
  'error.not-configured': 'The account server is not configured in this build.',

  // Shared
  'common.close': 'Close',
  'common.dismiss': 'Dismiss',
  'common.loading': 'Loading…',

  // Menu
  'nav.main': 'Main',
  'nav.home': 'Home',
  'nav.live': 'Live TV',
  'nav.movies': 'Movies',
  'nav.series': 'Series',
  'nav.search': 'Search',
  'nav.account': 'Account',
  'kind.live': 'Live',
  'kind.movie': 'Movie',
  'kind.series': 'Series',

  // Login backdrop tiles
  'tile.sports': 'Live sports',
  'tile.movies': 'Movies',
  'tile.series': 'Series',
  'tile.kids': 'Kids',
  'tile.music': 'Music',
  'tile.news': 'News',
  'tile.uhd': '4K UHD',
  'tile.docs': 'Documentaries',
  'tile.football': 'Football',
  'tile.cinema': 'Cinema',
  'tile.cartoons': 'Cartoons',
  'tile.concerts': 'Concerts',

  // Loading the channel list
  'sync.connecting': 'Connecting',
  'sync.downloading': 'Downloading the list',
  'sync.live': 'Loading live channels',
  'sync.movie': 'Loading movies',
  'sync.series': 'Loading series',
  'sync.saving': 'Saving',
  'sync.starting': 'Starting',
  'sync.items': '{count} items',
  'sync.of': '{done} of {total}',
  'sync.adding': 'Loading {name}',
  'sync.updating': 'Updating {name}',
  'sync.importing': 'Importing {name}',
  'sync.busy': 'The channel list is still updating. Wait for it to finish.',

  // Errors from the channel provider
  'errors.refused': 'The provider refused the request. Check that the account is active and not over its connection limit.',
  'errors.notFound': 'The address was not found on the server.',
  'errors.serverProblem': "The provider's server reported a problem. Make sure no other device is using this account, wait a minute, and try again.",
  'errors.network': 'Cannot reach the server. Check your internet connection.',
  'errors.timeout': 'The server took too long to respond. Try again later.',
  'errors.http': 'The server returned an error.',
  'errors.bad-credentials': 'Username or password is incorrect.',
  'errors.account-expired': 'This account has expired. Contact us.',
  'errors.account-disabled': 'This account is disabled. Contact us.',
  'errors.empty-playlist': 'The list contains no channels or videos.',
  'errors.parse': 'The list could not be read.',
  'errors.invalid-input': 'Some details are missing or invalid.',
  'errors.storage': 'Saving data on this device failed. Free up space and try again.',
  'errors.cancelled': 'Cancelled.',

  // Search
  'search.all': 'All',
  'search.placeholder': 'Channels, movies, series',
  'search.tooShort': 'Type at least {count} characters.',
  'search.none': 'No matches',
  'search.results': '{count} results',

  // Filters
  'filter.language': 'Language',
  'filter.allLanguages': 'All languages',
  'filter.country': 'Country',
  'filter.allCountries': 'All countries',

  // Browsing
  'browse.loadingChannels': 'Loading your channels… If nothing appears, open Account and tap Load channels.',
  'browse.source': 'Source',
  'browse.categories': 'Categories',
  'browse.other': 'Other',
  'browse.recent': 'Recently added',
  'browse.front': 'Featured',
  'browse.continue': 'Continue watching',
  'browse.seeAll': 'See all',
  'browse.previous': 'Previous',
  'browse.next': 'Next',
  'browse.episodes': 'Episodes',
  'browse.watched': 'Recently watched',
  'favorites.title': 'Favorites',
  'favorites.add': 'Add {name} to favorites',
  'favorites.remove': 'Remove {name} from favorites',
  'series.continue': 'Continue {episode} from {time}',
  'browse.allFiltered': 'All {filter}',
  'browse.empty.live': 'There are no live channels here.',
  'browse.empty.movie': 'There are no movies here.',
  'browse.empty.series': 'There are no series here.',
  'browse.unfinished': "{kind} didn't finish loading.",
  'browse.load.live': 'Load live channels',
  'browse.load.movie': 'Load movies',
  'browse.load.series': 'Load series',
  'browse.loading.live': 'Loading live channels…',
  'browse.loading.movie': 'Loading movies…',
  'browse.loading.series': 'Loading series…',
  'browse.meanwhile': 'Meanwhile, {count} live channels are ready in Live TV.',
  'series.loading': 'Loading episodes…',
  'series.seasons': 'Seasons',

  // Account
  'account.username': 'Username',
  'account.name': 'Name',
  'account.plan': 'Plan',
  'account.freeTrial': 'Free trial',
  'account.screens': 'Screens at the same time',
  'account.unlimited': 'Unlimited',
  'account.trialEndsIn': 'Trial ends in',
  'account.validUntil': 'Valid until',
  'account.trialNotStarted': '{length} · starts when you press play',
  'account.noEnd': 'No end date',
  'account.content': 'Content',
  'account.lastLoad': 'Last load',
  'account.lastLoadDetail': '{total} ({waiting} waiting for the server, {download} downloading, {storing} saving on this device)',
  'account.loading.live': 'channels loading…',
  'account.loading.movie': 'movies loading…',
  'account.loading.series': 'series loading…',
  'account.count.live': '{count} channels',
  'account.count.movie': '{count} movies',
  'account.count.series': '{count} series',
  'account.updateChannels': 'Update channels',
  'account.loadChannels': 'Load channels',
  'account.subscribe': 'Subscribe',
  'account.renew': 'Renew',
  'account.subscribeMessage': 'Hello, I would like to subscribe to IPTViva.',
  'account.renewMessage': 'Hello, I would like to renew my IPTViva subscription.',
  'account.helpMessage': 'Hello, I need help with IPTViva.',
  'account.help': 'Help',
  'account.helpText': "A question, a new device, or a channel that doesn't work? Contact us, we answer every day.",
  'account.appLanguage': 'App language',
  'account.channelLanguages': 'Channel languages',
  'account.international': 'international',
  'account.saveLanguages': 'Save and reload my list',
  'account.languagesSaved': 'Saved. Your channel list is reloading with these languages.',
  'account.changeLanguages': 'Change languages',
  'time.minutes': '{count} min',
  'time.hoursMinutes': '{hours} h {minutes} min',
  'time.days': '{count} days',
  'time.hour': '{count} hour',
  'time.hours': '{count} hours',

  // Home
  'home.loading': "Loading today's matches…",
  'home.failed': "Today's matches couldn't be loaded. This page tries again every minute.",
  'home.welcome': 'Welcome',
  'home.noMatches': 'No big football matches right now. They appear here as soon as we find channels showing them that play well.',
  'home.liveNow': 'Live now',
  'home.laterToday': 'Later today',
  'home.otherLeagues': 'Other',
  'home.favouriteLeague': 'Star {league}: show it first',
  'home.unfavouriteLeague': 'Remove {league} from favourites',
  'home.browse': 'Browse',
  'home.allChannels': 'All channels',
  'home.anyTime': 'Watch any time',
  'home.everyEpisode': 'Every episode',
  'home.todaysMatches': "Today's matches",
  'home.slideOf': '{position} of {count}',
  'home.previousMatch': 'Previous match',
  'home.nextMatch': 'Next match',
  'home.football': 'Football',
  'home.checking': 'Checking channels…',
  'home.channelIn': '{count} channel in {language}',
  'home.channelsIn': '{count} channels in {language}',
  'home.moreOther': '{count} more in other languages',
  'home.noChannel': 'No channel yet',
  'home.atKickoff': 'Channels appear at kick-off, once we have checked them',
  'home.channelsAtKickoff': 'Channels at kick-off',
  'home.tomorrow': 'Tomorrow',
  'home.days': 'Days',
  'home.comingUp': 'Coming up',
  'home.seeDay': 'See {day}',
  'home.channelsOnMatchDay': 'Channels on match day',
  'home.noMatchToday': 'No match today, next {date}',
  'home.noMatchDay': 'No match this day, next {date}',
  'home.noMatchesDay': 'No matches from the big leagues this day.',
  'home.noSchedule': "The schedule for the coming days isn't available yet.",
  'home.watchNow': 'Watch now',
  'home.details': 'Details',
  'home.today': 'Today',
  'home.inMinutes': 'in {count} min',
  'home.inHours': 'in {count} h',
  'home.oneChannel': '{count} channel',
  'home.channels': '{count} channels',
  'home.oneLanguage': '{count} language',
  'home.languages': '{count} languages',
  'home.checkingLong': "We're checking the channels showing this match. Working ones appear here in a minute or two.",
  'home.atKickoffLong': 'Channels appear here at kick-off, once we have checked that they play well.',
  'home.expectedOn': 'Expected on {name} · not checked yet',
  'home.expectedOnMore': 'Expected on {name} +{count} · not checked yet',
  'home.willBeOn': 'Not checked yet: these channels become playable once we have checked that they play well.',
  'home.mightBeOn': 'Might be on {name}',
  'home.mightBeOnMore': 'Might be on {name} +{count}',
  'home.mightHave': 'Might have the match: tap one to try it.',
  'home.mightHaveShort': 'Might have the match',
  'home.moreToTry': "More channels to try",
  'home.notChecked': 'Not checked',
  'home.searchMore': 'Search for more',
  'home.searchHint': "Not in the list? Search for the {channels} channels.",
  'home.searchHintClubs': "Not in the list? Search for the teams' club channels or the broadcaster.",
  'home.commentary': 'Commentary language',
  'home.playsSmoothly': 'Plays smoothly',
  'home.mayShowOther': 'May show another match',
  'home.guessesBefore': "The match is on one of the numbered channels, but we don't know which one yet. During the match, viewers help us find it.",
  'home.guessesLive': "The match is on one of the numbered channels, but we don't know which one yet. Try one: we'll ask you if the match is on it.",
  'home.confirmedByViewers': 'Confirmed by viewers',
  'player.matchQuestion': 'Is {match} on this channel?',
  'player.matchYes': 'Yes',
  'player.matchNo': 'No',
  'player.matchThanks': 'Thanks! Once enough viewers agree, everyone sees the answer.',
  'home.notConfirmed': 'Not confirmed: may be a youth game',
  'home.watchedNow': 'Watched now · starts instantly',
  'clock.live': 'LIVE',
  'clock.ht': 'HT',
  'clock.break': 'Break',
  'clock.penalties': 'Penalties',
  'clock.ft': 'FT',
  'clock.paused': 'Paused',

  // Player
  'player.buffering': 'Buffering…',
  'player.reconnecting': 'Reconnecting…',
  'player.paused': 'Paused',
  'player.pausedByOtherApp': 'Paused by another app (a call?). Tap Play to watch live.',
  'player.soundOffByOtherApp': 'Sound is off while another app (a call?) uses it. Tap to turn it on.',
  'player.soundOffShort': 'Sound off (a call?) · tap to turn on',
  'player.finished': 'Finished',
  'player.connecting': 'Connecting…',
  'player.queueTitle': 'Your free trial starts in',
  'player.queueNext': 'You are next in line.',
  'player.queuePosition': 'You are number {position} in line.',
  'player.queueBody': 'All trial spots are busy right now. Playback starts by itself, so keep this screen open. Your trial time starts when you get a spot.',
  'player.anyMoment': 'Any moment now…',
  'player.uhdTitle': "4K and 8K aren't supported on this device",
  'player.uhdBody': "This device can't play 4K or 8K, so this title isn't available here, and there's no HD version of it. Switch to another device, such as a newer TV, computer or phone, to get the full catalogue.",
  'browse.uhdHidden': "4K and 8K titles are hidden: this device can't play them. Switch to another device to get the full catalogue.",
  'browse.uhdPaused': "4K and 8K titles are paused for now: you see the HD versions. The LG and Samsung TV apps still have the full catalogue.",
  'player.uhdPausedTitle': "4K and 8K are paused for now",
  'player.uhdPausedBody': "4K and 8K titles aren't available here for now, and there's no HD version of this one. Try again later, or use our LG or Samsung TV app for the full catalogue.",
  'player.uhdPausedSwitched': "4K is paused for now: playing the HD version.",
  'player.uhdSwitched': "This device can't play 4K: playing the HD version instead.",
  'player.renewBody': 'Renew it to keep watching.',
  'player.trialEndedBody': 'We hope you enjoyed it. Subscribe to keep watching.',
  'player.fullOne': 'Already playing on {count} screen',
  'player.full': 'Already playing on {count} screens',
  'player.fullBodyOne': 'Your plan allows one screen at a time. Stop it to watch here.',
  'player.fullBody': 'Your plan allows {count} screens at the same time. Stop one of them to watch here.',
  'player.stopAndWatch': 'Stop {device} and watch here',
  'player.replacedTitle': 'Stopped from another screen',
  'player.replacedBody': 'Your plan allows a limited number of screens at the same time, and this one was stopped so another device could watch. Premium plans watch on two screens at once.',
  'player.watchHere': 'Watch here',
  'player.muted': 'Muted',
  'player.volumePercent': 'Volume {percent}%',
  'player.behindLive': '{time} behind live. Go live',
  'player.goLive': 'Go live',
  'player.prev': 'Prev',
  'player.next': 'Next',
  'player.previous': 'Previous',
  'player.previousChannel': 'Previous channel',
  'player.nextChannel': 'Next channel',
  'player.previousEpisode': 'Previous episode',
  'player.nextEpisode': 'Next episode',
  'player.back10': 'Back 10 seconds',
  'player.forward10': 'Forward 10 seconds',
  'player.play': 'Play',
  'player.pause': 'Pause',
  'player.audio': 'Audio',
  'player.subtitles': 'Subtitles',
  'player.info': 'Info',
  'player.fullscreen': 'Fullscreen',
  'player.off': 'Off',
  'player.quality': 'Quality',
  'player.qualityStandard': 'Standard',
  'player.qualityUnplayable': "{quality} doesn't play on this device; back to the previous quality.",
  'player.seek': 'Seek',
  'player.mute': 'Mute',
  'player.unmute': 'Unmute',
  'player.volume': 'Volume',
  'player.upNext': 'Up next: {name}',
  'player.playNext': 'Play next episode',
  'player.watchAgain': 'Watch again',
  'playback.network': 'The stream could not be reached. Check your connection or try again later.',
  'playback.stalled': 'The stream stopped sending video.',
  'playback.no-start': 'The stream did not start. The server may be down or too slow.',
  'playback.forbidden': 'The provider refused this stream. The account may have reached its connection limit.',
  'playback.not-found': 'This channel is no longer available from the provider.',
  'playback.unsupported': "This stream's format isn't supported on this device.",
  'playback.media': 'The video could not be decoded.',

  // Info panel
  'stats.title': 'Playback statistics',
  'stats.engine': 'Engine',
  'stats.resolution': 'Resolution',
  'stats.buffered': 'Buffered ahead',
  'stats.stalls': 'Stalls',
  'stats.dropped': 'Dropped frames',
  'stats.reconnects': 'Reconnects',
  'stats.sound': 'Sound',
  'stats.converted': 'converted to AAC',
  'stats.convertedFrom': 'converted to AAC from {codec}',
  'stats.original': 'original',
  'stats.started': 'Started in',
  'stats.problems': 'Last problems',
  'stats.none': 'none',
} as const;

export type MessageKey = keyof typeof en;
export type Messages = Record<MessageKey, string>;
